import { Checkbox, Modal, TextField } from '@issuu/ixu-react';
import {
    IconCheckCircle,
    IconEyeInvisible,
    IconPlus,
    layoutMarginSmall,
    ProductButton,
    ProductHeading,
    ProductParagraph,
    sizeBorderWidthSharedFocusInner,
    sizeBorderWidthSharedFocusOuter,
} from '@issuu/silkscreen';
import type { AddToStackRequestBody, Stack } from 'product-document-page-shared';
import React, { useCallback, useContext, useEffect, useReducer, useRef } from 'react';
import styled from 'styled-components';

import { addToStack } from '../../api';
import DocumentContext from '../DocumentPage/DocumentContext';
import { TextOverflow } from '../shared/styles';

const IconPrivate = styled(IconEyeInvisible)`
    vertical-align: middle;
    margin-left: 0.5em;
    flex: 0 0 auto;
`;

const SuccessMessage = styled.div`
    display: flex;
    height: 5em;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    justify-content: space-around;
`;

const StackItem = styled.li`
    display: flex;
    align-items: center;
    cursor: pointer;

    /* Style overrides to make text overflow work right */
    > button {
        flex: 0 1 auto;
        min-width: 0;
        justify-items: start;

        > :last-child {
            min-width: 0;
        }
    }
`;

const StackListWrapper = styled.div`
    position: relative;
    overflow: hidden;

    display: flex;
    flex-direction: column;
    max-height: 12.5rem;
    min-width: 20rem;
    user-select: none;
`;

const StackList = styled.ul`
    list-style-type: none;
    padding-block: ${layoutMarginSmall};
    padding-inline-start: calc(
        ${sizeBorderWidthSharedFocusInner} + ${sizeBorderWidthSharedFocusOuter}
    );
    margin: 0;
    overflow-y: auto;
    overflow-x: hidden;
    max-width: 80vw;

    &:before,
    &:after {
        content: '';
        left: 0;
        height: 1rem;
        position: absolute;
        pointer-events: none;
        width: 100%;
    }

    &:before {
        top: 0;
        background-image: linear-gradient(white, white 40%, transparent);
        z-index: 1;
    }

    &:after {
        bottom: 0;
        background-image: linear-gradient(transparent, white 60%, white);
    }
`;

const UnlistedChoice = styled.div`
    margin: ${layoutMarginSmall} 0;
    display: flex;
    width: 75rem;
    justify-content: space-between;
    align-items: center;
`;

const CreateForm = styled.div`
    display: flex;
    flex-direction: column;
    align-items: start;
`;

const StackTitle = styled(TextOverflow)`
    margin-left: ${layoutMarginSmall};
`;

export type Props = {
    isOpen: boolean;
    onClose: () => void;
};

type StackState = Readonly<{
    stacks: Stack[];
    showCreate: boolean;
    titleInMessage: string;
    newStackName: string;
    newAccess: Stack['access'];
}>;
type StackAction =
    | {
          type: 'setNewStackName';
          payload: string;
      }
    | {
          type: 'setNewAccess';
          payload: Stack['access'];
      }
    | {
          type: 'openCreate';
      }
    | {
          type: 'addToStack';
          payload: { stacks: Stack[]; title: string };
      }
    | {
          type: 'clear';
      };

const stackReducer: (state: StackState, action: StackAction) => StackState = (state, action) => {
    switch (action.type) {
        case 'openCreate':
            return { ...state, showCreate: true };
        case 'setNewStackName': {
            if (action.payload.length > 100) return state;
            return { ...state, newStackName: action.payload };
        }
        case 'setNewAccess':
            return { ...state, newAccess: action.payload };
        case 'addToStack':
            return {
                ...state,
                stacks: action.payload.stacks,
                titleInMessage: action.payload.title,
                showCreate: false,
            };
        case 'clear':
            return { ...state, titleInMessage: '', newStackName: '', showCreate: false };
    }
};

export const DocumentSaveModal: React.FC<Props> = ({ isOpen, onClose }: Props) => {
    const {
        document: { documentName },
        publisher,
        visitor,
    } = useContext(DocumentContext);

    const [{ stacks, showCreate, titleInMessage, newStackName, newAccess }, dispatch] = useReducer(
        stackReducer,
        {
            stacks: visitor.isLoggedIn ? visitor.stacks : [],
            showCreate: false,
            titleInMessage: '',
            newStackName: '',
            newAccess: 'public',
        },
    );

    const timer = useRef<number>(0);

    useEffect(() => {
        if (titleInMessage) {
            timer.current = window.setTimeout(() => {
                onClose();
                window.setTimeout(() => {
                    dispatch({ type: 'clear' });
                }, 200);
            }, 3000);
        }

        return () => {
            window.clearTimeout(timer.current);
        };
    }, [titleInMessage]);

    const addDocumentToStack = useCallback(
        (title: Stack['title'], request: AddToStackRequestBody): void => {
            addToStack(publisher.username, documentName, request).then(({ stacks }) => {
                dispatch({ type: 'addToStack', payload: { stacks, title } });
            });
        },
        [],
    );

    const dialogFocus = (wrapper: HTMLDivElement) => {
        window.setTimeout(() => {
            // Focus the first shown button in the dialog
            if (wrapper) {
                if (wrapper.querySelector('input')) {
                    wrapper.querySelector('input')?.focus();
                } else {
                    wrapper.querySelector('button')?.focus();
                }
            }
        }, 100);
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => {
                onClose();
                dispatch({ type: 'clear' });
                window.clearTimeout(timer.current);
            }}
        >
            <div data-testid="modalWrapper" ref={dialogFocus}>
                {titleInMessage ? (
                    <SuccessMessage>
                        <IconCheckCircle />
                        <span>
                            Successfully added to <strong>{titleInMessage}</strong>
                        </span>
                    </SuccessMessage>
                ) : (
                    <>
                        <ProductHeading level={4}>Save this to a stack</ProductHeading>
                        <StackListWrapper>
                            {stacks.length > 0 ? (
                                <StackList>
                                    {stacks.map(s => (
                                        <StackItem key={`stack_${s.id}`}>
                                            <ProductButton
                                                icon={<IconPlus />}
                                                onClick={() =>
                                                    addDocumentToStack(s.title, { id: s.id })
                                                }
                                                size="small"
                                                variant="ghost"
                                            >
                                                <StackTitle>
                                                    <ProductHeading level={4} step="negative-2">
                                                        {s.title}
                                                    </ProductHeading>
                                                </StackTitle>
                                            </ProductButton>
                                            {s.access === 'private' ? <IconPrivate /> : null}
                                        </StackItem>
                                    ))}
                                </StackList>
                            ) : (
                                <ProductParagraph>
                                    Organize publications into stacks to save your favorites.
                                </ProductParagraph>
                            )}
                        </StackListWrapper>
                        {showCreate ? (
                            <CreateForm>
                                <TextField
                                    onChange={name =>
                                        dispatch({ type: 'setNewStackName', payload: name })
                                    }
                                    value={newStackName}
                                />
                                <UnlistedChoice>
                                    <Checkbox
                                        checked={newAccess === 'private'}
                                        onChange={(v: boolean) =>
                                            dispatch({
                                                type: 'setNewAccess',
                                                payload: v ? 'private' : 'public',
                                            })
                                        }
                                        optimisticUpdate
                                    />
                                    Unlisted <IconEyeInvisible />
                                </UnlistedChoice>
                                <ProductButton
                                    disabled={!newStackName}
                                    onClick={() =>
                                        addDocumentToStack(newStackName, {
                                            title: newStackName,
                                            access: newAccess,
                                        })
                                    }
                                    variant="primary"
                                >
                                    Create
                                </ProductButton>
                            </CreateForm>
                        ) : (
                            <ProductButton
                                onClick={() => dispatch({ type: 'openCreate' })}
                                variant="primary"
                            >
                                Create New Stack
                            </ProductButton>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
};
